import {
  type CreateLockerverseAuctionOptions,
  createLockerverseAuctionClient,
  type LockerverseAuction,
  type LockerverseAuctionClient,
  type LockerverseSdkError,
} from "@lockerverse/sdk/auctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";

export type UseLockerverseAuctionResult = {
  client: LockerverseAuctionClient;
  /** Last successful snapshot, retained during refresh and refresh errors. */
  auction: LockerverseAuction | null;
  loading: boolean;
  error: LockerverseSdkError | null;
  refresh: () => void;
};

/** Load one auction by slug without requesting the community auction list. */
export function useLockerverseAuction(
  options: CreateLockerverseAuctionOptions,
  auctionSlug: string
): UseLockerverseAuctionResult {
  useImmutableResourceIdentity("auction", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    mediaBaseUrl: options.mediaBaseUrl,
    slug: auctionSlug,
  });
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const [client] = useState(() =>
    createLockerverseAuctionClient({
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    })
  );
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<
    Pick<UseLockerverseAuctionResult, "auction" | "loading" | "error">
  >({ auction: null, error: null, loading: true });

  useEffect(() => {
    let active = true;
    const requestGeneration = generation.current;
    client.load(auctionSlug).then(
      (auction) => {
        if (active && requestGeneration === generation.current) {
          setState({ auction, error: null, loading: false });
        }
      },
      (error: LockerverseSdkError) => {
        if (active && requestGeneration === generation.current) {
          setState((current) => ({ ...current, error, loading: false }));
        }
      }
    );
    return () => {
      active = false;
    };
  }, [client, auctionSlug, revision]);

  const refresh = useCallback(() => {
    generation.current += 1;
    setState((current) => ({ ...current, error: null, loading: true }));
    setRevision((current) => current + 1);
  }, []);

  return { ...state, client, refresh };
}
